import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import InAppSpy, { SFSVCExperimental } from 'inapp-spy';
import { useCallback, useEffect, useState } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';
import styled from 'styled-components';
import { MEDIUM } from '../utils/styled';
const EXTRA_INAPP_PATTERNS = [
    { regex: /\bSlack\b/i, name: 'Slack' },
    { regex: /\bDiscord\b/i, name: 'Discord' },
    { regex: /\bSignal\b/i, name: 'Signal' },
];
function detectInAppSync() {
    const { isInApp, appName } = InAppSpy();
    if (isInApp) {
        return { isInApp: true, appName };
    }
    const ua = navigator.userAgent;
    for (const { regex, name } of EXTRA_INAPP_PATTERNS) {
        if (regex.test(ua)) {
            return { isInApp: true, appName: name };
        }
    }
    return { isInApp: false, appName: undefined };
}
function getEscapeHref(currentUrl) {
    if (isIOS) {
        return currentUrl.replace(/^https:\/\//, 'x-safari-https://');
    }
    if (isAndroid) {
        try {
            const url = new URL(currentUrl);
            return `intent://${url.host}${url.pathname}${url.search}${url.hash}#Intent;scheme=https;end`;
        }
        catch {
            return undefined;
        }
    }
    return undefined;
}
function getBrowserName() {
    if (isIOS) {
        return 'Safari';
    }
    if (isAndroid) {
        return 'Chrome';
    }
    return 'your browser';
}
export function InAppBrowserGate({ children }) {
    const [detection, setDetection] = useState(() => {
        if (!isIOS && !isAndroid) {
            return { status: 'pass' };
        }
        const sync = detectInAppSync();
        if (sync.isInApp) {
            return { status: 'blocked', appName: sync.appName };
        }
        if (isIOS) {
            return { status: 'pending' };
        }
        return { status: 'pass' };
    });
    useEffect(() => {
        if (detection.status !== 'pending') {
            return;
        }
        let cancelled = false;
        // biome-ignore lint/nursery/noFloatingPromises: fire-and-forget async detection
        SFSVCExperimental({ maxTime: 400 }).then((isSFSVC) => {
            if (cancelled) {
                return;
            }
            if (isSFSVC) {
                setDetection({ status: 'blocked', appName: undefined });
            }
            else {
                setDetection({ status: 'pass' });
            }
        });
        return () => {
            cancelled = true;
        };
    }, [detection.status]);
    if (detection.status === 'pending') {
        return null;
    }
    if (detection.status === 'pass') {
        return _jsx(_Fragment, { children: children });
    }
    const currentUrl = window.location.href;
    const escapeHref = getEscapeHref(currentUrl);
    const browserName = getBrowserName();
    const sourceName = detection.appName ?? 'this app';
    return (_jsx(Overlay, { children: _jsxs(Card, { children: [_jsxs(Title, { children: ["Open in ", browserName] }), _jsxs(Description, { children: ["You\u2019re viewing this page inside ", sourceName, "\u2019s built-in browser, which doesn\u2019t support live video streaming \u2014 watching or broadcasting."] }), escapeHref && (_jsxs(OpenLink, { href: escapeHref, children: ["Open in ", browserName] })), _jsx(CopyUrlFallback, { url: currentUrl })] }) }));
}
function CopyUrlFallback({ url }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
        catch {
            // Clipboard API may not be available in some in-app browsers
        }
    }, [url]);
    return (_jsxs(FallbackSection, { children: [_jsx(FallbackLabel, { children: "Or copy the URL and paste it in your browser:" }), _jsxs(UrlRow, { children: [_jsx(UrlText, { children: url }), _jsx(CopyButton, { type: "button", onClick: handleCopy, children: copied ? 'Copied' : 'Copy' })] })] }));
}
const Overlay = styled.div `
    position: fixed;
    inset: 0;
    background-color: #000000;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
`;
const Card = styled.div `
    max-width: 26rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
`;
const Title = styled.h1 `
    margin: 0;
    color: #ffffff;
    font-size: 1.375rem;
    font-weight: ${MEDIUM};
    line-height: 1.75rem;
`;
const Description = styled.p `
    margin: 0;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9375rem;
    line-height: 1.375rem;
`;
const OpenLink = styled.a `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
    padding: 0 2rem;
    margin-top: 0.5rem;
    border-radius: 12px;
    background-color: #ffffff;
    color: #323232;
    font-size: 1rem;
    font-weight: ${MEDIUM};
    text-decoration: none;
    user-select: none;
    -webkit-tap-highlight-color: transparent;

    &:active {
        background-color: #e0e0e0;
    }
`;
const FallbackSection = styled.div `
    margin-top: 0.5rem;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;
const FallbackLabel = styled.span `
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.8125rem;
    line-height: 1.25rem;
`;
const UrlRow = styled.div `
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background-color: rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    overflow: hidden;
`;
const UrlText = styled.span `
    flex: 1;
    min-width: 0;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.75rem;
    line-height: 1rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    user-select: all;
`;
const CopyButton = styled.button `
    flex-shrink: 0;
    background: rgba(255, 255, 255, 0.15);
    border: none;
    border-radius: 6px;
    color: #ffffff;
    font-size: 0.8125rem;
    font-weight: ${MEDIUM};
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
    font-family: inherit;

    &:active {
        background: rgba(255, 255, 255, 0.25);
    }
`;
